import React, { useEffect, useState } from "react";
import CompanyLayout from "../../components/Company/CompanyLayout";
import Card from "../../components/UI/Card";
import Button from "../../components/UI/Button";
import CandidateCard from "../../components/Company/CandidateCard";
import CandidateFilters from "../../components/Company/CandidateFilters";
import * as companyResumeService from "../../services/companyResumeService";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

export default function Candidates() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    search: "",
    experienceLevel: [],
    skills: [],
    location: "",
    education: "",
    scoreMin: 0,
    scoreMax: 100,
    startDate: "",
    endDate: "",
  });
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState([]);
  const [view, setView] = useState("list");
  const [page, setPage] = useState(1);
  const [limit] = useState(12);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    fetchCandidates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  async function fetchCandidates() {
    setLoading(true);
    try {
      const res = await companyResumeService.getCompanyResumes({ page, limit });
      setCandidates(res.resumes || []);
      setTotal(res.total || 0);
    } catch {
      toast.error("Failed to load candidates");
    } finally {
      setLoading(false);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / limit));

  async function bulkStatus(newStatus) {
    if (!selected.length) {
      toast.error("No candidates selected");
      return;
    }
    try {
      await Promise.all(
        selected.map((id) =>
          companyResumeService.updateCandidateStatus(id, newStatus)
        )
      );
      toast.success(`Status updated for ${selected.length} candidates`);
      setSelected([]);
      await fetchCandidates();
    } catch {
      toast.error("Failed to update status");
    }
  }

  function exportCSV() {
    const rows = [["Name", "Location", "Score"]];
    for (const c of candidates) {
      rows.push([
        c.sections?.personalInfo?.name || "",
        c.sections?.personalInfo?.location || "",
        c.atsScore || 0,
      ]);
    }
    const csv = rows
      .map((r) => r.map((x) => `"${String(x).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "candidates.csv";
    link.click();
  }

  return (
    <CompanyLayout>
      <div className="space-y-6">
        <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <h1 className="text-2xl font-bold">Candidate Database</h1>
          <Button
            onClick={() => navigate("/company/resumes/upload")}
            className="w-full sm:w-auto"
          >
            Upload Resumes
          </Button>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          <div className="md:col-span-1 lg:col-span-1">
            <CandidateFilters
              filters={filters}
              onFilterChange={(f) => {
                setFilters(f);
                setPage(1);
                fetchCandidates();
              }}
              onClear={() => {
                setFilters({
                  search: "",
                  experienceLevel: [],
                  skills: [],
                  location: "",
                  education: "",
                  scoreMin: 0,
                  scoreMax: 100,
                  startDate: "",
                  endDate: "",
                });
                setPage(1);
                fetchCandidates();
              }}
            />
            <div className="mt-4 space-y-2">
              <Button
                variant="ghost"
                onClick={() => bulkStatus("shortlisted")}
                disabled={!selected.length}
              >
                Shortlist Selected
              </Button>
              <Button
                variant="ghost"
                onClick={() => bulkStatus("rejected")}
                disabled={!selected.length}
              >
                Reject Selected
              </Button>
              <Button variant="ghost" onClick={exportCSV}>
                Export CSV
              </Button>
            </div>
          </div>

          <div className="md:col-span-1 lg:col-span-3">
            <Card title={`Candidates (${total})`}>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <button
                    className={`px-2 py-1 rounded text-xs sm:text-sm ${
                      view === "list" ? "bg-gray-200" : ""
                    }`}
                    onClick={() => setView("list")}
                  >
                    List
                  </button>
                  <button
                    className={`px-2 py-1 rounded text-xs sm:text-sm ${
                      view === "grid" ? "bg-gray-200" : ""
                    }`}
                    onClick={() => setView("grid")}
                  >
                    Grid
                  </button>
                </div>
                <label className="flex items-center gap-2 text-xs sm:text-sm whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={
                      selected.length === candidates.length &&
                      candidates.length > 0
                    }
                    onChange={() =>
                      setSelected(
                        selected.length === candidates.length
                          ? []
                          : candidates.map((c) => c._id)
                      )
                    }
                  />
                  Select All
                </label>
              </div>
              {loading ? (
                <p className="text-sm text-gray-500">Loading...</p>
              ) : !candidates.length ? (
                <p className="text-sm text-gray-500">No candidates found.</p>
              ) : view === "list" ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-4 py-2">Select</th>
                        <th className="text-left px-4 py-2">Name</th>
                        <th className="text-left px-4 py-2">Skills</th>
                        <th className="text-left px-4 py-2">Location</th>
                        <th className="text-left px-4 py-2">Score</th>
                        <th className="text-left px-4 py-2">Uploaded</th>
                        <th className="text-left px-4 py-2">Status</th>
                        <th className="text-left px-4 py-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {candidates.map((c) => (
                        <tr key={c._id} className="border-t">
                          <td className="px-4 py-2">
                            <input
                              type="checkbox"
                              checked={selected.includes(c._id)}
                              onChange={(e) => {
                                if (e.target.checked)
                                  setSelected((s) => [...s, c._id]);
                                else
                                  setSelected((s) =>
                                    s.filter((id) => id !== c._id)
                                  );
                              }}
                            />
                          </td>
                          <td className="px-4 py-2">
                            {c.sections?.personalInfo?.name || "Candidate"}
                          </td>
                          <td className="px-4 py-2">
                            {(c.sections?.skills || [])
                              .flatMap((k) => k.items || [])
                              .slice(0, 5)
                              .join(", ")}
                          </td>
                          <td className="px-4 py-2">
                            {c.sections?.personalInfo?.location}
                          </td>
                          <td className="px-4 py-2">{c.atsScore || 0}%</td>
                          <td className="px-4 py-2">
                            {new Date(
                              c.createdAt || Date.now()
                            ).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-2">
                            {c.candidateStatus || "new"}
                          </td>
                          <td className="px-4 py-2">
                            <Button
                              variant="ghost"
                              onClick={() =>
                                navigate(`/company/candidates/${c._id}`)
                              }
                            >
                              View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {candidates.map((c) => (
                    <CandidateCard
                      key={c._id}
                      candidate={c}
                      selected={selected.includes(c._id)}
                      onSelect={(checked) => {
                        if (checked) setSelected((s) => [...s, c._id]);
                        else setSelected((s) => s.filter((id) => id !== c._id));
                      }}
                      onStatusChange={async (status) => {
                        await companyResumeService.updateCandidateStatus(
                          c._id,
                          status
                        );
                        await fetchCandidates();
                      }}
                      showMatchScore
                    />
                  ))}
                </div>
              )}
              <div className="mt-3 flex items-center justify-end gap-2">
                <Button
                  variant="ghost"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-500">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="ghost"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </CompanyLayout>
  );
}
